import { Link } from "react-router-dom";
import { ArrowLeft, Building2, Users, Sparkles, TrendingDown, Leaf, Award, MapPin, Store, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LogoOrText } from "@/lib/logo";

export default function QuemSomos() {
  return (
    <div className="min-h-screen bg-background text-foreground relative">
      <div className="absolute inset-0 grid-bg-lg opacity-30 pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="quem-somos-home">
            <LogoOrText className="h-10 max-w-[220px]" textClass="font-heading font-black tracking-tighter text-lg" />
          </Link>
          <Link to="/lojista/login">
            <Button className="rounded-sm bg-foreground text-background hover:bg-foreground/90 h-10" data-testid="quem-somos-portal">
              <Store size={14} className="mr-2" /> Portal do Lojista
            </Button>
          </Link>
        </div>
      </header>

      {/* Voltar */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="quem-somos-voltar">
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>

      {/* Bloco 1 — Quem Somos */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="inline-flex items-center gap-2 label-tag mb-4 border border-border px-3 py-1.5 rounded-full">
          <MapPin size={12} /> Magé · Rio de Janeiro · Estratégico
        </div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] break-anywhere max-w-4xl">
          <span className="text-muted-foreground">🏬</span> VM Piabetá Shopping Center<br />
          <span className="text-muted-foreground">O Hub Digital do Seu Negócio</span>
        </h1>

        <div className="mt-10 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3 space-y-8">
            <div>
              <div className="label-tag mb-3 flex items-center gap-2"><Users size={12} /> Quem Somos</div>
              <p className="text-base sm:text-lg leading-relaxed text-foreground/90 break-anywhere">
                O <strong>VM Piabetá Shopping Center</strong> é um ecossistema comercial planejado para conectar marcas fortes a um público qualificado.
                Nascido da necessidade de oferecer uma infraestrutura moderna, ágil e de alto fluxo na região de Magé,
                o empreendimento combina localização estratégica a um modelo operacional altamente eficiente.
              </p>
              <p className="text-base sm:text-lg leading-relaxed text-foreground/90 break-anywhere mt-4">
                Muito mais do que um espaço físico, somos um <strong>polo de convivência, conveniência e inovação tecnológica</strong>
                {" "}focado no sucesso e no crescimento sustentável dos nossos lojistas parceiros.
              </p>
            </div>

            <div>
              <div className="label-tag mb-3 flex items-center gap-2"><Building2 size={12} /> Breve Histórico</div>
              <p className="text-base sm:text-lg leading-relaxed text-foreground/90 break-anywhere">
                Projetado para transformar o cenário econômico local, o VM Piabetá Shopping consolidou-se como uma referência em comércio inteligente.
                O empreendimento foi desenhado com um conceito arquitetônico dinâmico de <strong>duas frentes de rua</strong>,
                otimizando o fluxo de pedestres e veículos.
              </p>
              <p className="text-base sm:text-lg leading-relaxed text-foreground/90 break-anywhere mt-4">
                Dividido de forma estratégica para abraçar desde o microempreendedor até grandes marcas, o complexo conta com
                {" "}<strong>65 pontos comerciais</strong>, distribuídos entre lojas satélites, lojas âncoras e estandes inteligentes em contêineres de padrão internacional.
                Desde a sua fundação, o shopping foca na dedicação total à gestão de excelência, integrando lojistas e administração em um ambiente transparente e moderno.
              </p>
            </div>
          </div>

          <aside className="lg:col-span-2">
            <div className="border border-border bg-card p-6 sticky top-24">
              <div className="label-tag mb-4">Nosso empreendimento</div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="stat-num text-5xl">65</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">Pontos Comerciais Planejados</div>
                </div>
                <div>
                  <div className="stat-num text-5xl">30</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">Estandes em Contêiner</div>
                </div>
                <div>
                  <div className="stat-num text-5xl">35</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">Lojas Âncoras e Satélites</div>
                </div>
                <div>
                  <div className="stat-num text-5xl">2</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">Frentes de rua ativas</div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Bloco 2 — O que é Strip Mall */}
      <section className="relative z-10 border-t border-border bg-card/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="max-w-3xl mb-12">
            <div className="label-tag mb-3 flex items-center gap-2"><Sparkles size={12} /> Nossa categoria de destaque</div>
            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-5xl leading-tight break-anywhere">
              🚀 O que é um Strip Mall?
            </h2>
            <p className="text-base text-muted-foreground mt-4 leading-relaxed break-anywhere">
              Um formato contemporâneo de varejo que otimiza custo, fluxo e experiência.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Store,
                title: "Conveniência e Acesso Direto",
                desc: "Pontos comerciais com forte apelo visual, frentes voltadas para a rua ou eixos de circulação aberta, facilitando o acesso rápido do consumidor que busca praticidade.",
              },
              {
                icon: TrendingDown,
                title: "Custo Operacional Reduzido",
                desc: "Estrutura arquitetônica mais eficiente e sustentável. O custo de condomínio e os rateios de manutenção são infinitamente mais enxutos do que em um shopping center convencional, maximizando a margem de lucro das lojas.",
              },
              {
                icon: Leaf,
                title: "Sustentabilidade e Fluidez",
                desc: "Uso de conceitos contemporâneos como módulos e contêineres inteligentes, garantindo agilidade nas instalações, estética moderna e flexibilidade para o varejo.",
              },
            ].map((f, i) => {
              const Ic = f.icon;
              return (
                <div key={f.title} className="stat-block p-6 rounded-sm relative overflow-hidden">
                  <div className="absolute top-4 right-4 label-tag opacity-60">0{i + 1}</div>
                  <div className="w-11 h-11 bg-foreground text-background flex items-center justify-center rounded-sm">
                    <Ic size={20} />
                  </div>
                  <h3 className="font-heading font-bold text-xl mt-5 tracking-tight break-anywhere">{f.title}</h3>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed break-anywhere">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bloco 3 — ABRASCE */}
      <section className="relative z-10 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="label-tag mb-3 flex items-center gap-2"><Award size={12} /> Credibilidade nacional</div>
              <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-5xl leading-tight break-anywhere">
                🏅 Selo ABRASCE
              </h2>
              <p className="text-base sm:text-lg text-foreground/90 leading-relaxed mt-6 break-anywhere">
                Refletindo o nosso compromisso com as melhores práticas de governança, compliance e desenvolvimento do setor,
                o <strong>VM Piabetá Shopping Center é associado à ABRASCE</strong> (Associação Brasileira de Shopping Centers).
              </p>
              <p className="text-base sm:text-lg text-foreground/90 leading-relaxed mt-4 break-anywhere">
                Fazer parte da principal entidade do setor no país certifica que a nossa gestão segue os mais rigorosos padrões de qualidade
                do mercado nacional. Para os nossos lojistas, isso se traduz em <strong>segurança jurídica</strong>, respaldo institucional
                e a certeza de estar operando em um shopping alinhado com as grandes referências do varejo brasileiro.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="border-2 border-dashed border-border p-10 rounded-sm bg-card/50 text-center min-h-[280px] flex flex-col items-center justify-center gap-4">
                <div className="w-20 h-20 border-2 border-foreground/20 rounded-full flex items-center justify-center">
                  <Award size={40} className="text-foreground/60" />
                </div>
                <div>
                  <div className="font-heading font-bold text-lg">Selo ABRASCE</div>
                  <div className="text-xs text-muted-foreground mt-1 uppercase tracking-widest">
                    Placeholder do selo oficial
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground font-mono mt-2 max-w-xs">
                  Espaço reservado para o selo digital oficial. O ativo será substituído pela imagem homologada da entidade.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="relative z-10 border-t border-border bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-col items-center text-center gap-6">
          <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl max-w-2xl leading-tight break-anywhere">
            Faça parte do VM Piabetá — cresça com quem entende do seu negócio.
          </h2>
          <Link to="/lojista/login">
            <Button size="lg" className="rounded-sm bg-background text-foreground hover:bg-background/90 h-14 px-10 text-base" data-testid="quem-somos-cta">
              Entrar no Portal do Lojista <ChevronRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="relative z-10 border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap justify-between items-center gap-4 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} VM Piabetá Shopping Center — Hub Digital do Lojista</div>
          <Link to="/admin/login" className="hover:text-foreground transition-colors" data-testid="footer-admin-link">
            Área da Administração
          </Link>
        </div>
      </footer>
    </div>
  );
}
